import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Camera,
  Check,
  CheckCircle,
  Download,
  Edit2,
  FileText,
  Plus,
  Printer,
  RefreshCw,
  Save,
  Settings,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalculationInput } from "@/components/CalculationInput";
import { DashboardLayout } from "@/components/DashboardLayout";
import { OfflineIndicator } from "@/components/offline/OfflineIndicator";
import { SyncStatus } from "@/components/offline/SyncStatus";
import PhotoUpload from "@/components/PhotoUpload";
import { ProjectDetailsHeader } from "@/components/ProjectDetailsHeader";
import QuoteChecklist from "@/components/QuoteChecklist";
import { type ScopeOfWorkData, ScopeOfWorkView } from "@/components/quote/ScopeOfWorkView";
import RunningTotal from "@/components/RunningTotal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { useChecklistState } from "@/hooks/quote-builder/useChecklistState";
import { type PhotoData, useImageGallery } from "@/hooks/quote-builder/useImageGallery";
import { useQuoteData } from "@/hooks/quote-builder/useQuoteData";
import { useQuoteMutations } from "@/hooks/quote-builder/useQuoteMutations";
import type { ServiceInstance } from "@/hooks/quote-builder/useServiceInstances";
// Refactored: Import custom hooks
import { useServiceInstances } from "@/hooks/quote-builder/useServiceInstances";
import { useToast } from "@/hooks/use-toast";
import { useOfflineQuote } from "@/hooks/useOfflineQuote";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { devLog, logError } from "@/lib/env";
import { fetchWithTimeout } from "@/lib/fetchUtils";
import type { ChecklistTemplate } from "@/types/components";
import {
  Checklist,
  type ChecklistComponent,
  type ChecklistResponseEntry,
  type ChecklistResponseValue,
  type ChecklistScalarValue,
  type RenovationService,
  type ServiceChecklist,
} from "@/types/quote.types";
import {
  extractScalarValue,
  flattenComponents as flattenComponentsUtil,
  isResponseEntry,
  serializeChecklistResponses,
} from "@/utils/quoteHelpers";

interface ChecklistManagementQuoteBuilderProps {
  projectId?: string;
  quoteId?: string | null;
  onBack?: () => void;
}

const ChecklistManagementQuoteBuilder = ({
  projectId,
  quoteId,
  onBack,
}: ChecklistManagementQuoteBuilderProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // REFACTORED: Use custom hooks for state management
  const serviceInstanceHook = useServiceInstances();
  const checklistStateHook = useChecklistState();
  const imageGalleryHook = useImageGallery();

  // Extract commonly used values for backward compatibility
  const {
    serviceInstances,
    activeInstanceId,
    setActiveInstanceId,
    addServiceInstance,
    removeServiceInstance,
    renameServiceInstance,
    getCurrentInstance,
    getInstancesByServiceId,
    getInstanceLabel,
    loadInstances,
  } = serviceInstanceHook;

  const {
    getInstanceResponses,
    setInstanceResponse,
    updateChecklistResponses,
    markChecklistComplete,
    unmarkChecklistComplete,
    getInstanceCompletedChecklists,
    isChecklistComplete,
    getAllResponses,
    getAllCompletedChecklists,
    updateQuantity: updateQuantityInHook,
    quantities,
    loadChecklistState,
  } = checklistStateHook;

  // Call the functions to get actual state objects
  const instanceResponses = getAllResponses();
  const instanceCompletedChecklists = getAllCompletedChecklists();

  const {
    setInstanceNotes,
    getInstanceNotes,
    setInstanceImages,
    getInstanceImages,
    addInstanceImage,
    removeInstanceImage,
    toggleGalleryExpanded,
    isGalleryExpanded,
    getAllGalleryData,
    loadGalleryData,
  } = imageGalleryHook;

  // Get image gallery data for compatibility
  const imageGalleryNotes = getAllGalleryData().notes;
  const imageGalleryImages = getAllGalleryData().images;

  // Offline support
  const isOnline = useOnlineStatus();

  // Remaining UI-only state
  const [activeService, setActiveService] = useState<number | null>(null);
  const [activeChecklist, setActiveChecklist] = useState<number | null>(null);
  const [completedChecklists, setCompletedChecklists] = useState<number[]>([]);
  const [checklistResponses, setChecklistResponses] = useState<
    Record<number, Record<string, ChecklistResponseValue>>
  >({});
  const [customChecklists, setCustomChecklists] = useState<
    Array<{ id: number; title: string; serviceId: number; wallByWall?: boolean }>
  >([]);
  const [newChecklistTitle, setNewChecklistTitle] = useState("");
  const [showAddChecklist, setShowAddChecklist] = useState(false);
  const [wallByWallEnabled, setWallByWallEnabled] = useState(false);
  const [currentQuoteId, setCurrentQuoteId] = useState<number | null>(null);
  const [isQuoteSummaryOpen, setIsQuoteSummaryOpen] = useState(false);
  const [quoteName, setQuoteName] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);

  // Rename dialog state
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [instanceToRename, setInstanceToRename] = useState<number | null>(null);
  const [renameInputValue, setRenameInputValue] = useState("");

  // Confirm delete dialog state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [instanceToDelete, setInstanceToDelete] = useState<number | null>(null);

  // Navigation state
  const [isNavigating, setIsNavigating] = useState(false);

  // Image gallery expanded state
  const [imageGalleryExpanded, setImageGalleryExpanded] = useState<Record<number, boolean>>({});

  // REFACTORED: Removed helper functions - now imported from @/utils/quoteHelpers
  const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

  // Helper functions for response entry building
  const extractQuantity = (
    checklistId: number,
    fieldName: string,
    value?: ChecklistResponseValue
  ): number | undefined => {
    if (value && isResponseEntry(value) && typeof value.quantity === "number") {
      return value.quantity;
    }

    return quantities[checklistId]?.[fieldName];
  };

  const buildResponseEntry = (
    checklistId: number,
    fieldName: string,
    value: ChecklistResponseValue
  ): ChecklistResponseEntry => {
    if (isResponseEntry(value)) {
      return value;
    }

    const quantity = extractQuantity(checklistId, fieldName, value);
    return quantity !== undefined
      ? { value: value as ChecklistScalarValue, quantity }
      : { value: value as ChecklistScalarValue };
  };

  // Fetch available templates
  const { data: templates = [] } = useQuery<ChecklistTemplate[]>({
    queryKey: ["templates"],
    queryFn: async () => {
      const response = await fetch(`${apiUrl}/api/v1/templates`);
      if (!response.ok) throw new Error("Failed to fetch templates");
      const result = await response.json();
      return result.data || [];
    },
  });

  // Load default template on mount
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
      // Find default bathroom template or first template
      const defaultTemplate =
        templates.find((t) => t.is_default && t.template_type === "bathroom") || templates[0];
      if (defaultTemplate) {
        setSelectedTemplateId(defaultTemplate.id);
        loadTemplateData(defaultTemplate);
      }
    }
  }, [templates]);

  // Invalidate checklist components cache when component mounts (e.g., coming from Checklist Management)
  useEffect(() => {
    // Check if we're coming from checklist management (you could pass this as a prop or use navigation state)
    queryClient.invalidateQueries({ queryKey: ["checklist-components"] });
  }, []); // Only run on mount

  // Fetch existing quote data if quoteId is provided
  const { data: existingQuote, isLoading: existingQuoteLoading } = useQuery({
    queryKey: ["existing-quote", quoteId],
    queryFn: async () => {
      if (!quoteId) return null;

      const response = await fetch(`${apiUrl}/api/v1/checklist-quotes/${quoteId}/summary`);
      if (!response.ok) throw new Error("Failed to fetch existing quote");

      return response.json();
    },
    enabled: !!quoteId,
  });

  // Load existing quote data into state when available
  useEffect(() => {
    if (existingQuote?.data && quoteId) {
      const quoteData = existingQuote.data;

      // Set current quote ID and name
      setCurrentQuoteId(Number.parseInt(quoteId, 10));
      setQuoteName(quoteData.quote.quote_name);

      // Load checklist responses from existing quote (legacy state)
      const responses: Record<number, Record<string, ChecklistResponseValue>> = {};
      const loadedQuantities: Record<number, Record<string, number>> = {};
      const completed: number[] = [];

      // Load service instances and instance-based responses
      const loadedInstances: ServiceInstance[] = [];
      const loadedInstanceResponses: Record<
        number,
        Record<number, Record<string, ChecklistResponseValue>>
      > = {};
      const loadedInstanceCompleted: Record<number, number[]> = {};
      const loadedImageGalleryImages: Record<number, PhotoData[]> = {};
      const loadedImageGalleryNotes: Record<number, string> = {};
      let instanceCounter = 1;

      quoteData.services?.forEach((service: any) => {
        // Create service instance
        const instanceId = instanceCounter++;
        loadedInstances.push({
          instanceId,
          serviceId: service.service_id,
          serviceName: service.service_name,
          customName: service.custom_name,
        });

        // Load checklist data for this instance
        const instanceChecklistResponses: Record<
          number,
          Record<string, ChecklistResponseValue>
        > = {};
        const instanceChecklistIds: number[] = [];

        service.checklists?.forEach((checklist: any) => {
          if (checklist.responses && Object.keys(checklist.responses).length > 0) {
            // Store in instance-based state
            instanceChecklistResponses[checklist.checklist_id] = checklist.responses;
            instanceChecklistIds.push(checklist.checklist_id);

            // Also store in legacy state for backward compatibility
            responses[checklist.checklist_id] = checklist.responses;
            completed.push(checklist.checklist_id);

            // Load quantities
            Object.entries(checklist.responses).forEach(
              ([fieldName, responseValue]: [string, ChecklistResponseValue]) => {
                if (isResponseEntry(responseValue) && typeof responseValue.quantity === "number") {
                  if (!loadedQuantities[checklist.checklist_id]) {
                    loadedQuantities[checklist.checklist_id] = {};
                  }
                  loadedQuantities[checklist.checklist_id][fieldName] = responseValue.quantity;
                }
              }
            );
          }
        });

        // Store instance data
        if (Object.keys(instanceChecklistResponses).length > 0) {
          loadedInstanceResponses[instanceId] = instanceChecklistResponses;
        }
        if (instanceChecklistIds.length > 0) {
          loadedInstanceCompleted[instanceId] = instanceChecklistIds;
        }

        // Load image gallery data for this instance
        if (service.image_gallery) {
          if (service.image_gallery.images && Array.isArray(service.image_gallery.images)) {
            loadedImageGalleryImages[instanceId] = service.image_gallery.images;
          }
          if (service.image_gallery.notes && typeof service.image_gallery.notes === "string") {
            loadedImageGalleryNotes[instanceId] = service.image_gallery.notes;
          }
        }
      });

      // Update all state using hooks
      loadInstances(loadedInstances, instanceCounter);
      loadChecklistState(loadedInstanceResponses, loadedInstanceCompleted, loadedQuantities);
      loadGalleryData(loadedImageGalleryImages, loadedImageGalleryNotes);

      // Legacy state (backward compatibility)
      setChecklistResponses(responses);
      setCompletedChecklists(completed);

      setHasUnsavedChanges(false); // Start fresh since we loaded existing data

      devLog("Quote loaded successfully", {
        quoteId,
        servicesCount: loadedInstances.length,
        instancesRestored: loadedInstances.map((i) => `${i.serviceName} (ID: ${i.instanceId})`),
      });

      toast({
        title: "Quote Loaded",
        description: `Loaded existing quote: ${quoteData.quote.quote_name} with ${loadedInstances.length} service(s)`,
      });
    }
  }, [existingQuote, quoteId]);

  // Fetch services from database
  const { data: services = [], isLoading: servicesLoading } = useQuery<RenovationService[]>({
    queryKey: ["renovation-services"],
    queryFn: async () => {
      const response = await fetch(`${apiUrl}/api/v1/checklists/services`);
      if (!response.ok) throw new Error("Failed to fetch services");
      const result = await response.json();
      // Extract data array from API response
      return result.data || result;
    },
  });

  // Fetch checklists for all services from database
  const { data: serviceChecklists = {}, isLoading: checklistsLoading } = useQuery<
    Record<number, ServiceChecklist[]>
  >({
    queryKey: ["service-checklists", services.map((s) => s.id)],
    queryFn: async () => {
      if (services.length === 0) return {};

      const allChecklists: Record<number, ServiceChecklist[]> = {};

      // Fetch checklists for each service
      await Promise.all(
        services.map(async (service) => {
          const response = await fetch(`${apiUrl}/api/v1/checklists/service/${service.id}`);
          if (response.ok) {
            const result = await response.json();
            allChecklists[service.id] = result.data || result;
          }
        })
      );

      return allChecklists;
    },
    enabled: services.length > 0,
  });

  // Fetch checklist components for all checklists from database
  const {
    data: checklistComponents = {},
    isLoading: componentsLoading,
    refetch: refetchComponents,
  } = useQuery<Record<number, ChecklistComponent[]>>({
    queryKey: ["checklist-components", serviceChecklists],
    queryFn: async () => {
      const allComponents: Record<number, ChecklistComponent[]> = {};

      // Ensure serviceChecklists is properly loaded before processing
      if (!serviceChecklists || typeof serviceChecklists !== "object") return {};

      // Get all checklist IDs from all services
      const allChecklistIds = Object.values(serviceChecklists)
        .filter((checklistArray) => Array.isArray(checklistArray))
        .flat()
        .map((checklist) => checklist?.id)
        .filter((id) => id !== undefined);

      if (allChecklistIds.length === 0) return {};

      // Fetch components for each checklist
      await Promise.all(
        allChecklistIds.map(async (checklistId) => {
          try {
            const response = await fetch(
              `${apiUrl}/api/v1/checklists/${checklistId}/components?hierarchical=true`
            );
            if (response.ok) {
              const result = await response.json();
              const components = result.data || result;
              if (components && Array.isArray(components) && components.length > 0) {
                allComponents[checklistId] = components;
              }
            }
          } catch (error) {
            console.warn(`Failed to fetch components for checklist ${checklistId}:`, error);
          }
        })
      );

      return allComponents;
    },
    enabled: serviceChecklists && Object.keys(serviceChecklists).length > 0,
    staleTime: 30000, // Consider data stale after 30 seconds
    cacheTime: 60000, // Keep in cache for 1 minute
  });

  // Load template data into the quote builder
  const loadTemplateData = (template: ChecklistTemplate) => {
    if (!template.template_data?.checklists) return;

    const templateChecklists = template.template_data.checklists;
    const newResponses: Record<number, Record<string, ChecklistResponseValue>> = {};
    const newQuantities: Record<number, Record<string, number>> = {};

    // Load template default values and quantities
    templateChecklists.forEach((checklist: any) => {
      const targetChecklistId = checklist.id || Date.now();

      checklist.components?.forEach((component: any) => {
        const defaultValue = component.default_value;
        const defaultQuantity = component.default_quantity;

        if (defaultValue !== undefined && defaultValue !== null) {
          if (!newResponses[targetChecklistId]) {
            newResponses[targetChecklistId] = {};
          }

          const quantityValue =
            component.allow_quantity_input && typeof defaultQuantity === "number"
              ? defaultQuantity
              : undefined;

          newResponses[targetChecklistId][component.field_name] =
            quantityValue !== undefined
              ? { value: defaultValue, quantity: quantityValue }
              : { value: defaultValue };

          if (quantityValue !== undefined) {
            if (!newQuantities[targetChecklistId]) {
              newQuantities[targetChecklistId] = {};
            }
            newQuantities[targetChecklistId][component.field_name] = quantityValue;
          }
        } else if (component.allow_quantity_input && typeof defaultQuantity === "number") {
          if (!newQuantities[targetChecklistId]) {
            newQuantities[targetChecklistId] = {};
          }
          newQuantities[targetChecklistId][component.field_name] = defaultQuantity;
        }
      });
    });

    setChecklistResponses(newResponses);
    // Load quantities via the hook's loadChecklistState method
    loadChecklistState({}, {}, newQuantities);
    setHasUnsavedChanges(true);

    toast({
      title: "Template Loaded",
      description: `Loaded template: ${template.name}`,
    });
  };

  // Handle template selection
  const handleTemplateChange = (templateId: string) => {
    const template = templates.find((t) => t.id === Number.parseInt(templateId, 10));
    if (template) {
      if (hasUnsavedChanges) {
        if (
          confirm(
            "You have unsaved changes. Loading a new template will reset your progress. Continue?"
          )
        ) {
          setSelectedTemplateId(template.id);
          loadTemplateData(template);
        }
      } else {
        setSelectedTemplateId(template.id);
        loadTemplateData(template);
      }
    }
  };

  // Update quantity handler - uses hook method
  const updateQuantity = (checklistId: number, fieldName: string, quantity?: number) => {
    // Use the hook method for quantity updates
    if (quantity !== undefined) {
      updateQuantityInHook(checklistId, fieldName, quantity);
    }

    // Update legacy responses state for backward compatibility
    setChecklistResponses((prev) => {
      const existing = prev[checklistId];
      if (!(existing && existing[fieldName])) {
        return prev;
      }

      const scalarValue = extractScalarValue(existing[fieldName]);
      if (scalarValue === undefined) {
        return prev;
      }

      const entry: ChecklistResponseEntry =
        quantity !== undefined ? { value: scalarValue, quantity } : { value: scalarValue };

      return {
        ...prev,
        [checklistId]: {
          ...existing,
          [fieldName]: entry,
        },
      };
    });

    setHasUnsavedChanges(true);
  };

  const flattenComponents = (components: ChecklistComponent[] = []): ChecklistComponent[] => {
    const result: ChecklistComponent[] = [];

    components.forEach((component) => {
      result.push(component);
      if (component.children && component.children.length > 0) {
        result.push(...flattenComponents(component.children));
      }
    });

    return result;
  };

  // Get all components for running total calculation
  const getAllComponentsForPricing = (): ChecklistComponent[] => {
    const allComponents: ChecklistComponent[] = [];

    Object.values(checklistComponents).forEach((components) => {
      allComponents.push(...flattenComponents(components));
    });

    return allComponents;
  };

  // REFACTORED: Service instance functions moved to useServiceInstances hook
  // REFACTORED: Checklist state functions moved to useChecklistState hook
  // REFACTORED: Mutations moved to useQuoteMutations hook (will be added below)

  // TODO: Replace these mutations with hook-based approach
  const createQuoteMutation = useMutation({
    mutationFn: async () => {
      if (!currentQuoteId) throw new Error("No active quote");

      // Prepare services data from service instances
      const servicesData = serviceInstances
        .map((instance) => {
          const serviceApiChecklists = serviceChecklists[instance.serviceId] || [];
          const customServiceChecklists = customChecklists.filter(
            (t) => t.serviceId === instance.serviceId
          );
          const allServiceChecklists = [...serviceApiChecklists, ...customServiceChecklists];

          const instanceCompleted = instanceCompletedChecklists[instance.instanceId] || [];
          const instanceData = instanceResponses[instance.instanceId] || {};

          // Get checklists with responses for this instance
          const checklistsWithData = allServiceChecklists
            .filter(
              (checklist) => instanceCompleted.includes(checklist.id) || instanceData[checklist.id]
            )
            .map((checklist) => ({
              checklist_id: checklist.id,
              checklist_name: checklist.name || checklist.title,
              responses: instanceData[checklist.id] || {},
            }));

          return {
            service_id: instance.serviceId,
            service_name: instance.serviceName,
            custom_name: instance.customName,
            image_gallery: {
              images: imageGalleryImages[instance.instanceId] || [],
              notes: imageGalleryNotes[instance.instanceId] || "",
            },
            checklists: checklistsWithData,
          };
        })
        .filter((service) => {
          // Keep services that have either checklists OR image gallery data
          const hasChecklists = service.checklists.length > 0;
          const hasImages = service.image_gallery.images.length > 0;
          const hasNotes = service.image_gallery.notes.trim().length > 0;
          return hasChecklists || hasImages || hasNotes;
        });

      // If no services have data, create empty placeholder to save the quote structure
      if (servicesData.length === 0) {
        devLog("No completed checklists yet, saving empty quote structure");
      }

      // Calculate total amount
      const totalAmount = servicesData.reduce((total, service) => {
        return (
          total +
          service.checklists.reduce((serviceTotal, checklist) => {
            const responses = Object.values(checklist.responses);
            return (
              serviceTotal +
              responses.reduce((sum, response) => {
                if (typeof response === "object" && response !== null && "price" in response) {
                  return sum + (Number(response.price) || 0);
                }
                return sum;
              }, 0)
            );
          }, 0)
        );
      }, 0);

      // If offline, save to IndexedDB
      if (!isOnline) {
        console.log("[ChecklistManagementQuoteBuilder] Offline - saving quote locally");

        const offlineResult = await createOfflineQuote({
          project_id: projectId!,
          checklist_template_id: currentQuoteId,
          quote_data: { services: servicesData },
          total_amount: totalAmount,
        });

        if (!offlineResult.success) {
          throw new Error(offlineResult.error || "Failed to save quote offline");
        }

        return {
          success: true,
          offline: true,
          quoteId: offlineResult.quoteId,
        };
      }

      // Online - save to API
      const response = await fetchWithTimeout(
        `${apiUrl}/api/v1/checklist-quotes/${currentQuoteId}/responses`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ services: servicesData }),
        },
        30000 // 30 second timeout for save operations
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Save quote error:", errorData);
        throw new Error(errorData.error || "Failed to save quote responses");
      }

      return response.json();
    },
    onSuccess: (data) => {
      devLog("Quote saved successfully:", data);
      setHasUnsavedChanges(false);

      if (data?.offline) {
        toast({
          title: "Quote Saved Offline",
          description: "Your quote will sync automatically when you're back online.",
        });
      } else {
        toast({
          title: "Quote Saved",
          description: "Your quote progress has been saved successfully.",
        });
      }
    },
    onError: (error: any) => {
      console.error("Quote save error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save quote",
        variant: "destructive",
      });
    },
  });

  // Save quote responses mutation
  const saveQuoteResponsesMutation = useMutation({
    mutationFn: async () => {
      if (!currentQuoteId) throw new Error("No active quote");

      // Prepare services data from service instances
      const servicesData = serviceInstances
        .map((instance) => {
          const serviceApiChecklists = serviceChecklists[instance.serviceId] || [];
          const customServiceChecklists = customChecklists.filter(
            (t) => t.serviceId === instance.serviceId
          );
          const allServiceChecklists = [...serviceApiChecklists, ...customServiceChecklists];

          const instanceCompleted = instanceCompletedChecklists[instance.instanceId] || [];
          const instanceData = instanceResponses[instance.instanceId] || {};

          // Get checklists with responses for this instance
          const checklistsWithData = allServiceChecklists
            .filter(
              (checklist) => instanceCompleted.includes(checklist.id) || instanceData[checklist.id]
            )
            .map((checklist) => ({
              checklist_id: checklist.id,
              checklist_name: checklist.name || checklist.title,
              responses: instanceData[checklist.id] || {},
            }));

          return {
            service_id: instance.serviceId,
            service_name: instance.serviceName,
            custom_name: instance.customName,
            image_gallery: {
              images: imageGalleryImages[instance.instanceId] || [],
              notes: imageGalleryNotes[instance.instanceId] || "",
            },
            checklists: checklistsWithData,
          };
        })
        .filter((service) => {
          // Keep services that have either checklists OR image gallery data
          const hasChecklists = service.checklists.length > 0;
          const hasImages = service.image_gallery.images.length > 0;
          const hasNotes = service.image_gallery.notes.trim().length > 0;
          return hasChecklists || hasImages || hasNotes;
        });

      // If no services have data, log it
      if (servicesData.length === 0) {
        devLog("No completed checklists yet, saving empty quote structure");
      }

      // Online - save to API
      const response = await fetchWithTimeout(
        `${apiUrl}/api/v1/checklist-quotes/${currentQuoteId}/responses`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ services: servicesData }),
        },
        30000 // 30 second timeout for save operations
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("Save quote error:", errorData);
        throw new Error(errorData.error || "Failed to save quote responses");
      }

      return response.json();
    },
    onSuccess: () => {
      devLog("Quote saved successfully");
      toast({
        title: "Quote Saved",
        description: "Your quote progress has been saved successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["quotes"] });
      queryClient.invalidateQueries({ queryKey: ["quote-summary"] });
      setHasUnsavedChanges(false);
    },
    onError: (error: any) => {
      console.error("Quote save error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save quote",
        variant: "destructive",
      });
    },
  });

  // Fetch quote summary
  const { data: quoteSummary, refetch: refetchSummary } = useQuery({
    queryKey: ["quote-summary", currentQuoteId],
    queryFn: async () => {
      if (!currentQuoteId) return null;

      const response = await fetchWithTimeout(
        `${apiUrl}/api/v1/checklist-quotes/${currentQuoteId}/summary`,
        {},
        15000 // 15 second timeout for fetching summaries
      );
      if (!response.ok) throw new Error("Failed to fetch quote summary");

      return response.json();
    },
    enabled: !!currentQuoteId && isQuoteSummaryOpen,
  });

  // Fetch project data for quote summary
  const { data: projectQuery } = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => {
      if (!projectId) return null;

      const response = await fetchWithTimeout(`${apiUrl}/api/v1/projects/${projectId}`, {}, 10000);
      if (!response.ok) throw new Error("Failed to fetch project");

      return response.json();
    },
    enabled: !!projectId && isQuoteSummaryOpen,
  });

  // Get checklists for active service instance including custom checklists
  const getServiceChecklists = () => {
    const currentInstance = getCurrentInstance();
    if (!currentInstance) return [];
    const apiChecklists = serviceChecklists[currentInstance.serviceId] || [];
    const customServiceChecklists = customChecklists.filter(
      (t) => t.serviceId === currentInstance.serviceId
    );
    return [...apiChecklists, ...customServiceChecklists];
  };

  // Move to next checklist in the list
  const moveToNextChecklist = () => {
    if (!activeInstanceId) return;

    const allChecklists = getServiceChecklists().filter(
      (checklist) => checklist.title !== "📷 Project Image Gallery"
    );

    if (allChecklists.length === 0) {
      setActiveChecklist(null);
      return;
    }

    // If no active checklist, open the first one
    if (activeChecklist === null) {
      setActiveChecklist(allChecklists[0].id);
      return;
    }

    // Find current checklist index
    const currentIndex = allChecklists.findIndex((c) => c.id === activeChecklist);
    if (currentIndex === -1) {
      // Current checklist not found, open first
      setActiveChecklist(allChecklists[0].id);
      return;
    }

    // Find next uncompleted checklist
    const instanceCompleted = instanceCompletedChecklists[activeInstanceId] || [];
    const remaining = allChecklists.slice(currentIndex + 1);
    const nextIncomplete = remaining.find((c) => !instanceCompleted.includes(c.id));

    if (nextIncomplete) {
      setActiveChecklist(nextIncomplete.id);
    } else if (remaining.length > 0) {
      // No incomplete, but there are more checklists - open next one
      setActiveChecklist(remaining[0].id);
    } else {
      // No more checklists, close the active one
      setActiveChecklist(null);
    }
  };

  const updateChecklistResponse = (
    checklistId: number,
    fieldName: string,
    value: ChecklistResponseValue
  ) => {
    const entry = buildResponseEntry(checklistId, fieldName, value);

    if (!activeInstanceId) return;

    // Update instance responses using the hook method
    setInstanceResponse(activeInstanceId, checklistId, fieldName, entry);

    // Legacy state - keep updated for backward compatibility
    setChecklistResponses((prev) => ({
      ...prev,
      [checklistId]: {
        ...prev[checklistId],
        [fieldName]: entry,
      },
    }));

    // Update quantity using hook if quantity is provided
    if (entry.quantity !== undefined) {
      updateQuantityInHook(checklistId, fieldName, entry.quantity);
    }

    setHasUnsavedChanges(true);
  };

  const toggleChecklistComplete = (checklistId: number) => {
    if (!activeInstanceId) return;

    const instanceCompleted = instanceCompletedChecklists[activeInstanceId] || [];
    const isCompleted = instanceCompleted.includes(checklistId);

    if (isCompleted) {
      // Remove from completed
      unmarkChecklistComplete(activeInstanceId, checklistId);
    } else {
      // Add to completed
      markChecklistComplete(activeInstanceId, checklistId);
      toast({
        title: "Checklist Completed",
        description: "Checklist has been marked as complete",
      });
    }

    // Legacy state - keep updated for backward compatibility
    if (completedChecklists.includes(checklistId)) {
      setCompletedChecklists((prev) => prev.filter((id) => id !== checklistId));
    } else {
      setCompletedChecklists((prev) => [...prev, checklistId]);
    }

    setActiveChecklist(null);
  };

  const unconfirmChecklist = (checklistId: number) => {
    if (!activeInstanceId) return;

    // Remove from instance completed checklists
    unmarkChecklistComplete(activeInstanceId, checklistId);

    // Clear responses for this checklist in this instance
    updateChecklistResponses(activeInstanceId, checklistId, {});

    // Legacy state - keep updated for backward compatibility
    setCompletedChecklists((prev) => prev.filter((id) => id !== checklistId));
    setChecklistResponses((prev) => {
      const updated = { ...prev };
      delete updated[checklistId];
      return updated;
    });

    // Quantities are managed by the hook via updateChecklistResponses above

    // Mark as having unsaved changes
    setHasUnsavedChanges(true);

    toast({
      title: "Checklist Unconfirmed",
      description: "Checklist has been marked as incomplete and component values have been cleared",
    });
  };

  const addCustomChecklist = () => {
    const currentInstance = getCurrentInstance();
    if (newChecklistTitle.trim() && currentInstance) {
      const newChecklist = {
        id: Date.now(),
        title: newChecklistTitle,
        serviceId: currentInstance.serviceId,
        wallByWall: wallByWallEnabled,
      };
      setCustomChecklists((prev) => [...prev, newChecklist]);
      setNewChecklistTitle("");
      setShowAddChecklist(false);
      setWallByWallEnabled(false);
      setHasUnsavedChanges(true);
      toast({
        title: "Checklist Added",
        description: wallByWallEnabled
          ? "Custom checklist with wall-by-wall calculation has been added successfully"
          : "Custom checklist has been added successfully",
      });
    }
  };

  // Automatically create image gallery for each service instance
  useEffect(() => {
    const currentInstance = getCurrentInstance();
    if (!currentInstance) return;

    // Check if image gallery already exists for this service
    const existingGallery = customChecklists.find(
      (c) => c.title === "📷 Project Image Gallery" && c.serviceId === currentInstance.serviceId
    );

    // If not, create it automatically
    if (!existingGallery) {
      const imageGalleryChecklist = {
        id: -Date.now(), // Negative ID to ensure it sorts first
        title: "📷 Project Image Gallery",
        serviceId: currentInstance.serviceId,
      };

      setCustomChecklists((prev) => [imageGalleryChecklist, ...prev]);
      // Set expanded by default
      setImageGalleryExpanded((prev) => ({
        ...prev,
        [imageGalleryChecklist.id]: true,
      }));
    }
  }, [activeInstanceId, getCurrentInstance]);

  const addImageGalleryChecklist = () => {
    // This function is now deprecated - gallery is added automatically
    // Keeping it to avoid breaking references, but it does nothing
    return;
  };

  // Quote handler functions
  const handleCreateQuote = async () => {
    if (!quoteName.trim()) {
      toast({
        title: "Quote Name Required",
        description: "Please enter a name for your quote.",
        variant: "destructive",
      });
      return;
    }

    // If editing an existing quote, just update the local state
    if (quoteId && currentQuoteId) {
      // Update quote name via API (you might want to add an update endpoint)
      // For now, just update local state and show success
      setHasUnsavedChanges(true);
      toast({
        title: "Quote Updated",
        description: `Quote name updated to "${quoteName}".`,
      });
      return;
    }

    // Create new quote
    createQuoteMutation.mutate({
      quote_name: quoteName,
      notes: "Quote for bathroom renovation project",
    });
  };

  const handleSaveQuote = async () => {
    try {
      // If no quote exists, create one first
      if (!currentQuoteId) {
        const defaultQuoteName = `Bathroom Renovation Quote - ${new Date().toLocaleDateString()}`;
        const result = await createQuoteMutation.mutateAsync({
          quote_name: defaultQuoteName,
          notes: "Quote for bathroom renovation project",
        });

        // Wait a moment for the state to update, then save responses
        setTimeout(() => {
          saveQuoteResponsesMutation.mutate();
        }, 100);

        return;
      }

      // Save responses for existing quote
      saveQuoteResponsesMutation.mutate();
    } catch (error) {
      console.error("Error saving quote:", error);
      toast({
        title: "Error",
        description: "Failed to save quote. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleViewSummary = () => {
    if (!currentQuoteId) {
      toast({
        title: "Create Quote First",
        description: "Please create a quote before viewing summary.",
        variant: "destructive",
      });
      return;
    }

    setIsQuoteSummaryOpen(true);
    refetchSummary();
  };

  const handleGenerateQuote = async () => {
    try {
      // If no quote exists, create one first
      if (!currentQuoteId) {
        const defaultQuoteName = `Bathroom Renovation Quote - ${new Date().toLocaleDateString()}`;
        await createQuoteMutation.mutateAsync({
          quote_name: defaultQuoteName,
          notes: "Auto-generated quote for bathroom renovation project",
        });
        // The mutation will set currentQuoteId, so we need to wait for it
        // The summary will be handled after the quote is created
        return;
      }

      // Save all checklist responses first, then generate quote
      await saveQuoteResponsesMutation.mutateAsync();
      handleViewSummary();
    } catch (error) {
      console.error("Error generating quote:", error);
      toast({
        title: "Error",
        description: "Failed to generate quote. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Navigation handlers
  const handleBackNavigation = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      navigateToProjectDetails();
    }
  };

  const navigateToProjectDetails = () => {
    if (onBack) {
      onBack();
      return;
    }

    if (projectId) {
      navigate(`/projects/${projectId}`);
    } else {
      navigate("/");
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      await handleSaveQuote();
      setShowUnsavedDialog(false);
      navigateToProjectDetails();
    } catch (error) {
      // Error handling is already in handleSaveQuote
    }
  };

  const handleLeaveWithoutSaving = () => {
    setShowUnsavedDialog(false);
    setHasUnsavedChanges(false);
    navigateToProjectDetails();
  };

  const handleBackToServices = async () => {
    // Prevent double-clicks during save
    if (isNavigating) {
      return;
    }

    setIsNavigating(true);

    // Auto-save changes before navigating back to services
    if (hasUnsavedChanges && currentQuoteId) {
      try {
        await saveQuoteResponsesMutation.mutateAsync();

        // Invalidate related queries to refresh data
        await queryClient.invalidateQueries({
          queryKey: ["quote-summary", currentQuoteId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["existing-quote", String(currentQuoteId)],
        });

        toast({
          title: "Changes Saved",
          description: "Your progress has been saved automatically.",
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        devLog("Error auto-saving:", { error, quoteId: currentQuoteId });

        // Ask user if they want to continue without saving
        const shouldContinue = window.confirm(
          "Failed to save changes. Do you want to continue anyway? Unsaved work will be lost."
        );

        if (!shouldContinue) {
          setIsNavigating(false);
          return; // Stop - don't navigate if save failed and user cancels
        }

        toast({
          title: "Warning",
          description: "Navigating without saving changes.",
          variant: "destructive",
        });
      }
    }

    // Navigate back to services view only after successful save or user confirmation
    setActiveInstanceId(null);
    setActiveChecklist(null);
    setIsNavigating(false);
  };

  const calculateServiceProgress = (serviceId: number): number => {
    const apiChecklists = serviceChecklists[serviceId] || [];
    const customServiceChecklists = customChecklists.filter((t) => t.serviceId === serviceId);
    const allChecklists = [...apiChecklists, ...customServiceChecklists];

    if (allChecklists.length === 0) return 0;
    const completed = completedChecklists.filter((checklistId) =>
      allChecklists.some((checklist) => checklist.id === checklistId)
    ).length;
    return Math.round((completed / allChecklists.length) * 100);
  };

  // Get services that have been activated (have responses or completed checklists)
  // Now returns ServiceInstance[] to support multiple instances of same service
  const getActiveServices = (): ServiceInstance[] => {
    return serviceInstances;
  };

  const calculateOverallProgress = (): number => {
    const activeServices = getActiveServices();
    let totalChecklists = 0;
    let completedCount = 0;

    activeServices.forEach((instance) => {
      const apiChecklists = serviceChecklists[instance.serviceId] || [];
      const customServiceChecklists = customChecklists.filter(
        (t) => t.serviceId === instance.serviceId
      );
      const allChecklists = [...apiChecklists, ...customServiceChecklists];

      totalChecklists += allChecklists.length;
      const instanceCompleted = instanceCompletedChecklists[instance.instanceId] || [];
      completedCount += instanceCompleted.length;
    });

    if (totalChecklists === 0) return 0;
    return Math.round((completedCount / totalChecklists) * 100);
  };

  if (servicesLoading || existingQuoteLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">
          {existingQuoteLoading ? "Loading quote..." : "Loading services..."}
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      {projectId && <ProjectDetailsHeader currentProjectId={projectId} />}

      {/* Template Selector - Hidden per requirement */}
      {/* {templates.length > 0 && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-semibold text-blue-900">Quote Template</h3>
              <p className="text-sm text-blue-700">
                Choose a template to load predefined checklists and pricing
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => window.open("/templates", "_blank")}>
              <Settings className="w-4 h-4 mr-2" />
              Manage Templates
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="template-selector">Select Template</Label>
              <Select
                value={selectedTemplateId?.toString() || ""}
                onValueChange={handleTemplateChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={template.id.toString()}>
                      <div className="flex items-center gap-2">
                        {template.name}
                        {template.is_default && (
                          <Badge variant="secondary" className="text-xs">
                            Default
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTemplateId && (
              <>
                <div>
                  <Label>Template Type</Label>
                  <div className="text-sm font-medium capitalize">
                    {templates
                      .find((t) => t.id === selectedTemplateId)
                      ?.template_type?.replace("_", " ")}
                  </div>
                </div>
                <div>
                  <Label>Last Updated</Label>
                  <div className="text-sm">
                    {new Date(
                      templates.find((t) => t.id === selectedTemplateId)?.updated_at || ""
                    ).toLocaleDateString()}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )} */}

      {/* Back Navigation and Action Buttons */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={handleBackNavigation}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Project Details
          </Button>
          <OfflineIndicator />
        </div>

        <div className="flex items-center gap-3">
          <SyncStatus />
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              refetchComponents();
              toast({
                title: "Checklists Refreshed",
                description: "Checklist components have been updated from Checklist Management.",
              });
            }}
            disabled={componentsLoading}
          >
            {componentsLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Checklists
              </>
            )}
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={handleSaveQuote}
            disabled={saveQuoteResponsesMutation.isPending || serviceInstances.length === 0}
          >
            {saveQuoteResponsesMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Quote
              </>
            )}
          </Button>

          <Button
            size="sm"
            onClick={handleGenerateQuote}
            disabled={completedChecklists.length === 0}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Download className="w-4 h-4 mr-2" />
            Generate Quote Summary
          </Button>
        </div>
      </div>

      <div className="flex">
        {/* Persistent Sidebar - Always Visible */}
        <div className="w-80 bg-white border-r min-h-screen p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">Service Progress</h3>
            <Select
              onValueChange={(value) => {
                const serviceId = Number.parseInt(value, 10);
                const service = services.find((s) => s.id === serviceId);
                if (service) {
                  addServiceInstance(service.id, service.name);
                }
              }}
            >
              <SelectTrigger className="h-10 px-3 py-2 border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 transition-colors">
                <div className="flex items-center gap-2">
                  <Plus className="w-5 h-5 text-gray-600" />
                  <span className="text-sm font-medium text-gray-700">Add</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id.toString()}>
                    {service.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium">Overall Progress</span>
              <span className="text-sm font-semibold">{calculateOverallProgress()}%</span>
            </div>
            <Progress value={calculateOverallProgress()} className="h-2" />
            <p className="text-xs text-gray-500 mt-1">
              {Object.values(instanceCompletedChecklists).reduce((sum, arr) => sum + arr.length, 0)}{" "}
              checklists completed
            </p>
          </div>

          <div className="space-y-3">
            {getActiveServices().length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-8">
                <p>No services selected yet.</p>
                <p className="mt-2">Select a service below to get started.</p>
              </div>
            ) : (
              getActiveServices().map((instance) => {
                const apiChecklists = serviceChecklists[instance.serviceId] || [];
                const customServiceChecklists = customChecklists.filter(
                  (t) => t.serviceId === instance.serviceId
                );
                const allChecklists = [...apiChecklists, ...customServiceChecklists];
                const instanceCompleted = instanceCompletedChecklists[instance.instanceId] || [];
                const completedCount = instanceCompleted.length;
                const progress =
                  allChecklists.length > 0
                    ? Math.round((completedCount / allChecklists.length) * 100)
                    : 0;
                const isActive = activeInstanceId === instance.instanceId;

                return (
                  <div
                    key={instance.instanceId}
                    className={`p-3 rounded-lg border transition-all ${
                      isActive ? "border-blue-500 bg-blue-50" : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4
                        className="font-medium text-sm cursor-pointer flex-1"
                        onClick={() => setActiveInstanceId(instance.instanceId)}
                      >
                        {getInstanceLabel(instance.instanceId)}
                      </h4>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground font-semibold">
                          {progress}%
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setInstanceToRename(instance.instanceId);
                            setRenameInputValue(getInstanceLabel(instance.instanceId));
                            setRenameDialogOpen(true);
                          }}
                          className="text-gray-400 hover:text-blue-500 transition-colors p-1"
                          title="Rename service instance"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setInstanceToDelete(instance.instanceId);
                            setConfirmDeleteOpen(true);
                          }}
                          className="text-gray-400 hover:text-red-500 transition-colors p-1"
                          title="Remove service"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <Progress value={progress} className="mb-2 h-2" />
                    <p className="text-xs text-muted-foreground">
                      {completedCount} of {allChecklists.length} checklists completed
                    </p>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 p-6">
          {activeInstanceId === null ? (
            /* Service Selection with Large Square Cards */
            <div>
              {/* In-Progress Services Section */}
              {serviceInstances.length > 0 && (
                <>
                  <div className="mb-6">
                    <h2 className="text-2xl font-semibold mb-2">
                      Continue Working On Quote Services
                    </h2>
                    <p className="text-gray-600">
                      Click on a service to continue editing your in-progress quote
                    </p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-12">
                    {serviceInstances.map((instance) => {
                      const service = services.find((s) => s.id === instance.serviceId);
                      if (!service) return null;

                      const apiChecklists = serviceChecklists[instance.serviceId] || [];
                      const customServiceChecklists = customChecklists.filter(
                        (t) => t.serviceId === instance.serviceId
                      );
                      const allChecklists = [...apiChecklists, ...customServiceChecklists];
                      const instanceCompleted =
                        instanceCompletedChecklists[instance.instanceId] || [];
                      const completedCount = instanceCompleted.length;
                      const progress =
                        allChecklists.length > 0
                          ? Math.round((completedCount / allChecklists.length) * 100)
                          : 0;
                      const isCompleted = progress === 100;

                      return (
                        <Card
                          key={instance.instanceId}
                          className="aspect-square cursor-pointer hover:shadow-lg transition-all duration-200 border-green-500 bg-green-50 hover:bg-green-100"
                          onClick={() => setActiveInstanceId(instance.instanceId)}
                        >
                          <CardContent className="p-6 h-full flex flex-col justify-between">
                            <div className="flex-1 flex flex-col items-center justify-center text-center">
                              <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4 bg-green-500">
                                {isCompleted ? (
                                  <Check className="w-10 h-10 text-white" />
                                ) : (
                                  <span className="text-3xl font-bold text-white">
                                    {service.name.charAt(0)}
                                  </span>
                                )}
                              </div>
                              <h3 className="font-semibold text-lg mb-2">
                                {getInstanceLabel(instance.instanceId)}
                              </h3>
                              <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                                {service.description || "Configure checklists for this service"}
                              </p>
                            </div>
                            <div className="w-full">
                              <div className="flex justify-between text-sm mb-2">
                                <span className="text-gray-500">Progress</span>
                                <span className="font-semibold">{progress}%</span>
                              </div>
                              <Progress value={progress} className="h-2 mb-2" />
                              <p className="text-xs text-center text-gray-500">
                                {completedCount} of {allChecklists.length} checklists completed
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Available Services Section */}
              <div className="mb-6">
                <h2 className="text-2xl font-semibold mb-2">Add Quote Services</h2>
                <p className="text-gray-600">Choose additional services to include in your quote</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {services.map((service) => {
                  const progress = calculateServiceProgress(service.id);
                  const isCompleted = progress === 100;
                  const apiChecklists = serviceChecklists[service.id] || [];
                  const customServiceChecklists = customChecklists.filter(
                    (t) => t.serviceId === service.id
                  );
                  const allChecklists = [...apiChecklists, ...customServiceChecklists];

                  return (
                    <Card
                      key={service.id}
                      className="aspect-square cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-blue-500"
                      onClick={() => addServiceInstance(service.id, service.name)}
                    >
                      <CardContent className="p-6 h-full flex flex-col justify-between">
                        <div className="flex-1 flex flex-col items-center justify-center text-center">
                          <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4 bg-blue-500">
                            <span className="text-3xl font-bold text-white">
                              {service.name.charAt(0)}
                            </span>
                          </div>
                          <h3 className="font-semibold text-lg mb-2">{service.name}</h3>
                          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                            {service.description || "Configure checklists for this service"}
                          </p>
                        </div>
                        <div className="w-full">
                          <p className="text-xs text-center text-gray-500">
                            {allChecklists.length} checklists available
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Checklist Management - BathroomQuote Style */
            <div>
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToServices}
                    disabled={isNavigating || saveQuoteResponsesMutation.isPending}
                  >
                    {isNavigating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Services
                      </>
                    )}
                  </Button>
                  <div>
                    <h2 className="text-2xl font-semibold">
                      {getCurrentInstance()?.serviceName || "Service"} Checklists
                    </h2>
                    <p className="text-gray-600">Click on a checklist to configure details</p>
                  </div>
                </div>

                {/* Add Custom Checklist Button */}
                {!showAddChecklist && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowAddChecklist(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Add Custom Checklist
                    </Button>
                  </div>
                )}
              </div>

              {/* Add Custom Checklist Input */}
              {showAddChecklist && (
                <Card className="mb-4">
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <Input
                        placeholder="Enter custom checklist title..."
                        value={newChecklistTitle}
                        onChange={(e) => setNewChecklistTitle(e.target.value)}
                        onKeyPress={(e) =>
                          e.key === "Enter" && !wallByWallEnabled && addCustomChecklist()
                        }
                        autoFocus={true}
                      />
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="wallByWall"
                          checked={wallByWallEnabled}
                          onChange={(e) => setWallByWallEnabled(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300"
                        />
                        <Label htmlFor="wallByWall" className="text-sm font-normal cursor-pointer">
                          Enable wall-by-wall calculation
                        </Label>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={addCustomChecklist}>Add Checklist</Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowAddChecklist(false);
                            setNewChecklistTitle("");
                            setWallByWallEnabled(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Image Gallery Section - Dedicated Area at Top */}
              {customChecklists.some(
                (c) =>
                  c.title === "📷 Project Image Gallery" &&
                  c.serviceId === getCurrentInstance()?.serviceId
              ) && (
                <Card className="mb-6 border-2 border-blue-200 bg-blue-50/30">
                  <CardHeader
                    className="cursor-pointer hover:bg-blue-50/50 transition-colors"
                    onClick={() => {
                      const gallery = customChecklists.find(
                        (c) =>
                          c.title === "📷 Project Image Gallery" &&
                          c.serviceId === getCurrentInstance()?.serviceId
                      );
                      if (gallery) {
                        setImageGalleryExpanded((prev) => ({
                          ...prev,
                          [gallery.id]: !prev[gallery.id],
                        }));
                      }
                    }}
                  >
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Camera className="w-6 h-6 text-blue-600" />
                        <span className="text-lg font-semibold text-blue-900">Project Images</span>
                        <span className="text-sm text-gray-600 font-normal">
                          {imageGalleryImages[activeInstanceId!]?.length || 0}/40
                        </span>
                      </div>
                      <ArrowRight
                        className={`w-5 h-5 text-blue-600 transition-transform ${
                          imageGalleryExpanded[
                            customChecklists.find(
                              (c) =>
                                c.title === "📷 Project Image Gallery" &&
                                c.serviceId === getCurrentInstance()?.serviceId
                            )?.id || 0
                          ]
                            ? "rotate-90"
                            : ""
                        }`}
                      />
                    </CardTitle>
                  </CardHeader>
                  {imageGalleryExpanded[
                    customChecklists.find(
                      (c) =>
                        c.title === "📷 Project Image Gallery" &&
                        c.serviceId === getCurrentInstance()?.serviceId
                    )?.id || 0
                  ] && (
                    <CardContent className="pt-0">
                      {(() => {
                        const gallery = customChecklists.find(
                          (c) =>
                            c.title === "📷 Project Image Gallery" &&
                            c.serviceId === getCurrentInstance()?.serviceId
                        );
                        if (!gallery) return null;

                        return (
                          <div className="space-y-4 p-4 bg-white rounded-lg">
                            <div>
                              <Label htmlFor={`gallery-notes-${gallery.id}`}>Notes</Label>
                              <Textarea
                                id={`gallery-notes-${gallery.id}`}
                                placeholder="Add notes about the project, site conditions, or special requirements..."
                                value={imageGalleryNotes[activeInstanceId!] || ""}
                                onChange={(e) => {
                                  setImageGalleryNotes((prev) => ({
                                    ...prev,
                                    [activeInstanceId!]: e.target.value,
                                  }));
                                  setHasUnsavedChanges(true);
                                }}
                                rows={4}
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <Label>Project Images</Label>
                              <div className="mt-2">
                                <PhotoUpload
                                  componentId={activeInstanceId!}
                                  fieldName={`gallery-images-${activeInstanceId}`}
                                  value={imageGalleryImages[activeInstanceId!] || []}
                                  onChange={(photos) => {
                                    // PhotoUpload now returns PhotoData[] with {url, caption}
                                    const photoArray = Array.isArray(photos) ? photos : [photos];
                                    setInstanceImages(activeInstanceId!, photoArray);
                                    setHasUnsavedChanges(true);
                                  }}
                                  allowMultiple={true}
                                  maxFiles={40}
                                  projectId={projectId}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </CardContent>
                  )}
                </Card>
              )}

              {/* Checklist List - BathroomQuote Style */}
              <div className="space-y-4">
                {getServiceChecklists()
                  .filter((checklist) => checklist.title !== "📷 Project Image Gallery")
                  .map((checklist) => {
                    const instanceCompleted = instanceCompletedChecklists[activeInstanceId!] || [];
                    const isCompleted = instanceCompleted.includes(checklist.id);
                    const isActive = activeChecklist === checklist.id;
                    const isCustom = customChecklists.some((t) => t.id === checklist.id);

                    return (
                      <Card
                        key={checklist.id}
                        className={`transition-all duration-200 ${
                          isCompleted ? "border-green-500" : ""
                        } ${isActive ? "shadow-lg" : "hover:shadow-md cursor-pointer"}`}
                      >
                        <CardHeader
                          className="pb-4 cursor-pointer"
                          onClick={() => setActiveChecklist(isActive ? null : checklist.id)}
                        >
                          <CardTitle className="flex items-center justify-between text-lg">
                            <div className="flex items-center space-x-3">
                              <div
                                className={`flex items-center justify-center w-8 h-8 rounded-full ${
                                  isCompleted ? "bg-green-500" : "bg-gray-200"
                                }`}
                              >
                                {isCompleted ? (
                                  <CheckCircle className="w-5 h-5 text-white" />
                                ) : (
                                  <span className="text-sm font-semibold text-gray-600">
                                    {customChecklists.findIndex((t) => t.id === checklist.id) !== -1
                                      ? `C${customChecklists.findIndex((t) => t.id === checklist.id) + 1}`
                                      : serviceChecklists[
                                          getCurrentInstance()?.serviceId || 0
                                        ]?.findIndex((t) => t.id === checklist.id) + 1 || ""}
                                  </span>
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="font-medium flex items-center gap-2">
                                  {isCustom
                                    ? checklist.title
                                    : (checklist as ServiceChecklist).name}
                                  {isCustom && (
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                                      Custom
                                    </span>
                                  )}
                                </div>
                                {!isCustom && (checklist as ServiceChecklist).description && (
                                  <div className="text-sm text-gray-600 font-normal mt-1">
                                    {(checklist as ServiceChecklist).description}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="text-sm text-gray-500">
                              {isCompleted ? (
                                <span className="text-green-600 font-medium">Completed</span>
                              ) : isActive ? (
                                <ArrowLeft className="w-4 h-4" />
                              ) : (
                                <ArrowRight className="w-4 h-4" />
                              )}
                            </div>
                          </CardTitle>
                        </CardHeader>

                        {/* Expanded Checklist Details */}
                        {isActive && (
                          <CardContent className="pt-0">
                            {!isCustom && (
                              <div className="mb-4">
                                {checklistComponents[checklist.id] &&
                                checklistComponents[checklist.id].length > 0 ? (
                                  <QuoteChecklist
                                    key={`checklist-${checklist.id}`}
                                    checklist={{
                                      id: checklist.id,
                                      title: (checklist as ServiceChecklist).name,
                                      service_id: (checklist as ServiceChecklist).service_id,
                                      is_active: (checklist as ServiceChecklist).is_active,
                                      type: "dynamic" as const,
                                      components: checklistComponents[checklist.id] || [],
                                      wall_by_wall: (checklist as ServiceChecklist).wall_by_wall,
                                    }}
                                    responses={getInstanceResponses(
                                      activeInstanceId!,
                                      checklist.id
                                    )}
                                    quantities={quantities}
                                    onQuantityChange={updateQuantity}
                                    isCompleted={isCompleted}
                                    onResponseChange={(
                                      fieldName: string,
                                      value: ChecklistResponseValue
                                    ) => {
                                      updateChecklistResponse(checklist.id, fieldName, value);
                                    }}
                                    onChecklistComplete={(checklistId: number) => {
                                      // Auto-complete the checklist when user confirms
                                      if (!isCompleted) {
                                        markChecklistComplete(activeInstanceId!, checklistId);

                                        // Legacy state for backward compatibility
                                        setCompletedChecklists((prev) => [...prev, checklistId]);

                                        // Log checklist responses for debugging (will be saved when quote is generated)
                                        const responses = getInstanceResponses(
                                          activeInstanceId!,
                                          checklistId
                                        );
                                        devLog(
                                          `Checklist ${checklistId} completed with responses:`,
                                          responses
                                        );

                                        toast({
                                          title: "Checklist Completed",
                                          description:
                                            "Checklist has been saved and marked as complete",
                                        });
                                      }
                                      setActiveChecklist(null); // Close the checklist
                                    }}
                                    onChecklistUnconfirm={(checklistId: number) => {
                                      unconfirmChecklist(checklistId);
                                      setActiveChecklist(null); // Close the checklist
                                    }}
                                    onMoveToNextChecklist={moveToNextChecklist}
                                    onCancel={() => setActiveChecklist(null)}
                                  />
                                ) : (
                                  <div className="text-sm text-gray-500">
                                    No components configured for this checklist
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Only show close button for custom checklists without components */}
                            {(isCustom ||
                              !checklistComponents[checklist.id] ||
                              checklistComponents[checklist.id].length === 0) && (
                              <div className="space-y-4">
                                {/* Special rendering for Image Gallery checklist */}
                                {checklist.title === "📷 Project Image Gallery" && (
                                  <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                                    <div>
                                      <Label htmlFor={`gallery-notes-${checklist.id}`}>Notes</Label>
                                      <Textarea
                                        id={`gallery-notes-${checklist.id}`}
                                        placeholder="Add notes about the project, site conditions, or special requirements..."
                                        value={imageGalleryNotes[activeInstanceId!] || ""}
                                        onChange={(e) => {
                                          setImageGalleryNotes((prev) => ({
                                            ...prev,
                                            [activeInstanceId!]: e.target.value,
                                          }));
                                          setHasUnsavedChanges(true);
                                        }}
                                        rows={4}
                                        className="mt-1"
                                      />
                                    </div>
                                    <div>
                                      <Label>Project Images</Label>
                                      <div className="mt-2">
                                        <PhotoUpload
                                          componentId={activeInstanceId!}
                                          fieldName={`gallery-images-${activeInstanceId}`}
                                          value={imageGalleryImages[activeInstanceId!] || []}
                                          onChange={(photos) => {
                                            // PhotoUpload now returns PhotoData[] with {url, caption}
                                            const photoArray = Array.isArray(photos)
                                              ? photos
                                              : [photos];
                                            setInstanceImages(activeInstanceId!, photoArray);
                                            setHasUnsavedChanges(true);
                                          }}
                                          allowMultiple={true}
                                          maxFiles={40}
                                          projectId={projectId}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Wall-by-Wall Calculation for custom checklists */}
                                {isCustom && (checklist as any).wallByWall && (
                                  <div className="mb-4">
                                    <CalculationInput
                                      component={
                                        {
                                          id: checklist.id,
                                          checklist_id: checklist.id,
                                          component_type: "number",
                                          field_name: "_wall_calculation",
                                          label: "Wall-by-Wall Area Calculation",
                                          is_required: false,
                                          display_order: 0,
                                          calculation_formula:
                                            "((shortWallLength * shortWallHeight) * 2) + ((longWallLength * longWallHeight) * 2) + (includeFloor * floorLength * floorWidth) * (1 + (wastagePercent / 100))",
                                          calculation_inputs: JSON.stringify([
                                            "shortWallLength",
                                            "shortWallHeight",
                                            "longWallLength",
                                            "longWallHeight",
                                            "floorLength",
                                            "floorWidth",
                                            "includeFloor",
                                            "wastagePercent",
                                          ]),
                                          calculation_display_unit: "m²",
                                          calculation_decimal_places: 2,
                                        } as any
                                      }
                                      value={
                                        getInstanceResponses(activeInstanceId!, checklist.id)[
                                          "_wall_calculation"
                                        ] || {}
                                      }
                                      onChange={(value) => {
                                        devLog("Wall calculation updated", value);
                                        updateChecklistResponse(
                                          checklist.id,
                                          "_wall_calculation",
                                          value
                                        );
                                        setHasUnsavedChanges(true);
                                      }}
                                    />
                                  </div>
                                )}

                                <div className="flex gap-3">
                                  <Button
                                    onClick={() => toggleChecklistComplete(checklist.id)}
                                    variant={isCompleted ? "outline" : "default"}
                                  >
                                    {isCompleted ? "Mark Incomplete" : "Mark Complete"}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    onClick={() => setActiveChecklist(null)}
                                  >
                                    Close
                                  </Button>
                                </div>
                              </div>
                            )}
                          </CardContent>
                        )}
                      </Card>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Action Buttons for Quote Management */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-50 pb-safe" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)' }}>
        {/* Create Quote Button */}
        {!currentQuoteId && (
          <Dialog>
            <DialogTrigger asChild={true}>
              <Button className="rounded-full w-14 h-14 shadow-lg" size="icon">
                <FileText className="w-6 h-6" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{quoteId ? "Edit Quote" : "Create New Quote"}</DialogTitle>
                <DialogDescription>
                  {quoteId
                    ? "Update the name for your quote."
                    : "Enter a name for your quote to get started."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label htmlFor="quote-name" className="block text-sm font-medium mb-1">
                    Quote Name
                  </label>
                  <Input
                    id="quote-name"
                    placeholder="e.g., Master Bathroom Renovation"
                    value={quoteName}
                    onChange={(e) => setQuoteName(e.target.value)}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <DialogTrigger asChild={true}>
                    <Button variant="outline">Cancel</Button>
                  </DialogTrigger>
                  <Button onClick={handleCreateQuote} disabled={createQuoteMutation.isPending}>
                    {createQuoteMutation.isPending
                      ? quoteId
                        ? "Updating..."
                        : "Creating..."
                      : quoteId
                        ? "Update Quote"
                        : "Create Quote"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Save Quote Progress Button */}
        {currentQuoteId && (
          <Button
            className="rounded-full w-14 h-14 shadow-lg bg-blue-600 hover:bg-blue-700"
            size="icon"
            onClick={handleSaveQuote}
            disabled={saveQuoteResponsesMutation.isPending}
            title="Save Quote Progress"
          >
            <Save className="w-6 h-6" />
          </Button>
        )}

        {/* Generate Quote Summary Button */}
        {currentQuoteId && (
          <Button
            className="rounded-full w-14 h-14 shadow-lg bg-green-600 hover:bg-green-700"
            size="icon"
            onClick={handleViewSummary}
            title="View Quote Summary"
          >
            <Download className="w-6 h-6" />
          </Button>
        )}
      </div>

      {/* Quote Summary Modal - Using ScopeOfWorkView */}
      <Dialog open={isQuoteSummaryOpen} onOpenChange={setIsQuoteSummaryOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-0">
          {quoteSummary?.data ? (
            <div className="p-6">
              <ScopeOfWorkView
                data={{
                  ...quoteSummary.data,
                  project: projectQuery.data || undefined,
                }}
                showPrintHeader={true}
              />

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6 pt-6 border-t">
                <Button variant="outline" onClick={() => setIsQuoteSummaryOpen(false)}>
                  Close
                </Button>
                <Button onClick={() => window.print()} className="bg-blue-600 hover:bg-blue-700">
                  <Printer className="w-4 h-4 mr-2" />
                  Print Quote
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center py-8">
              <p className="text-gray-500">Loading quote summary...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Dialog */}
      <Dialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Unsaved Changes
            </DialogTitle>
            <DialogDescription>
              You have unsaved changes in your quote. What would you like to do?
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-4">
            <Button
              onClick={handleSaveAndLeave}
              className="w-full"
              disabled={saveQuoteResponsesMutation.isPending}
            >
              {saveQuoteResponsesMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save & Go Back
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleLeaveWithoutSaving} className="w-full">
              Leave Without Saving
            </Button>
            <Button variant="ghost" onClick={() => setShowUnsavedDialog(false)} className="w-full">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Running Total - Commented out as it's distracting on tablets */}
      {/* <RunningTotal
        components={getAllComponentsForPricing()}
        responses={Object.entries(checklistResponses).reduce(
          (acc, [checklistKey, responses]) => {
            const parsedId = Number.parseInt(checklistKey, 10);
            const checklistId = Number.isNaN(parsedId) ? 0 : parsedId;
            Object.entries(responses || {}).forEach(([fieldName, responseValue]) => {
              acc[fieldName] = buildResponseEntry(checklistId, fieldName, responseValue);
            });
            return acc;
          },
          {} as Record<string, ChecklistResponseEntry>
        )}
        quantities={quantities}
        position="fixed"
        className="max-w-sm"
      /> */}

      {/* Rename Service Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Service Instance</DialogTitle>
            <DialogDescription>
              Enter a custom name for this service (e.g., "Master Bathroom", "Main Floor Kitchen")
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rename-input">Service Name</Label>
              <Input
                id="rename-input"
                value={renameInputValue}
                onChange={(e) => setRenameInputValue(e.target.value)}
                placeholder="Enter custom name"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && instanceToRename !== null && renameInputValue.trim()) {
                    renameServiceInstance(instanceToRename, renameInputValue);
                    setRenameDialogOpen(false);
                  }
                }}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (instanceToRename !== null && renameInputValue.trim()) {
                  renameServiceInstance(instanceToRename, renameInputValue);
                  setRenameDialogOpen(false);
                }
              }}
              disabled={!renameInputValue.trim()}
            >
              Rename
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Service Instance?</DialogTitle>
            <DialogDescription>
              {instanceToDelete !== null && (
                <>
                  Are you sure you want to remove{" "}
                  <strong>{getInstanceLabel(instanceToDelete)}</strong>? All checklist data for this
                  service will be lost.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setConfirmDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (instanceToDelete !== null) {
                  removeServiceInstance(instanceToDelete);
                  setConfirmDeleteOpen(false);
                  toast({
                    title: "Service removed",
                    description: "The service instance has been removed.",
                  });
                }
              }}
            >
              Remove
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default ChecklistManagementQuoteBuilder;
